import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { buildScenarioPrompt } from "../api";
import { getAccessToken, isAuthenticated, login, logout, refreshIfNeeded } from "../auth";
import { WS_BOT_URL } from "../config";
import { createVoicebotWsService } from "../services/voicebotWsService";

const TARGET_RATE = 16000;
const PLAYBACK_LEAD_SEC = 0.12;
const PLAYBACK_MIN_CHUNK_SAMPLES = Math.floor(TARGET_RATE * 0.06);
const PLAYBACK_MAX_CHUNK_SAMPLES = Math.floor(TARGET_RATE * 0.18);
const PLAYBACK_FLUSH_DELAY_MS = 20;

function defaultWsUrl() {
  if (WS_BOT_URL) return WS_BOT_URL;

  const isHttp = window.location.protocol === "http:" || window.location.protocol === "https:";
  if (isHttp) {
    const proto = window.location.protocol === "https:" ? "wss" : "ws";
    if (window.location.port === "8000") {
      return `${proto}://${window.location.host}/ws/bot`;
    }
    return `${proto}://${window.location.hostname}:8000/ws/bot`;
  }
  return "ws://localhost:8000/ws/bot";
}

function isInsecureProdWsUrl(url) {
  if (process.env.NODE_ENV !== "production") return false;
  return /^ws:\/\//i.test(String(url || "").trim());
}

function downsampleToTarget(data, sourceRate) {
  if (sourceRate === TARGET_RATE || sourceRate < TARGET_RATE) return data;

  const ratio = sourceRate / TARGET_RATE;
  const newLength = Math.round(data.length / ratio);
  const out = new Float32Array(newLength);
  let offsetResult = 0;
  let offsetBuffer = 0;

  while (offsetResult < out.length) {
    const nextOffsetBuffer = Math.round((offsetResult + 1) * ratio);
    let accum = 0;
    let count = 0;
    for (let i = offsetBuffer; i < nextOffsetBuffer && i < data.length; i += 1) {
      accum += data[i];
      count += 1;
    }
    out[offsetResult] = count ? accum / count : 0;
    offsetResult += 1;
    offsetBuffer = nextOffsetBuffer;
  }
  return out;
}

function float32ToInt16(data) {
  const out = new Int16Array(data.length);
  for (let i = 0; i < data.length; i += 1) {
    const v = Math.max(-1, Math.min(1, data[i]));
    out[i] = v < 0 ? v * 0x8000 : v * 0x7fff;
  }
  return out;
}

function concatInt16(a, b) {
  if (!a.length) return b;
  if (!b.length) return a;
  const out = new Int16Array(a.length + b.length);
  out.set(a, 0);
  out.set(b, a.length);
  return out;
}

export default function useVoicebotWs() {
  const wsUrl = useMemo(() => defaultWsUrl(), []);

  const [state, setState] = useState("idle");
  const [busy, setBusy] = useState(false);
  const [connected, setConnected] = useState(false);
  const [sessionStarted, setSessionStarted] = useState(false);
  const [micActive, setMicActive] = useState(false);
  const [transcripts, setTranscripts] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [warningMessage, setWarningMessage] = useState("");

  const serviceRef = useRef(null);

  const audioCtxRef = useRef(null);
  const micStreamRef = useRef(null);
  const micSourceRef = useRef(null);
  const scriptNodeRef = useRef(null);
  const micSinkGainRef = useRef(null);

  const playbackAtRef = useRef(0);
  const playbackPendingRef = useRef(new Int16Array(0));
  const playbackFlushTimerRef = useRef(null);
  const playbackDrainingRef = useRef(false);

  const sessionStartedRef = useRef(false);
  const stopRequestedRef = useRef(false);
  const unauthorizedPendingRef = useRef(false);
  const retryCountRef = useRef(0);
  const retryInProgressRef = useRef(false);
  const loginRedirectPendingRef = useRef(false);
  const systemPromptRef = useRef("");
  const sessionVarsRef = useRef({});
  const openConnectionRef = useRef(null);

  const addTranscript = useCallback((kind, text) => {
    const item = {
      id: `${Date.now()}_${Math.random().toString(16).slice(2)}`,
      kind,
      text: String(text || ""),
    };
    setTranscripts((prev) => {
      const next = [...prev, item];
      return next.length > 300 ? next.slice(next.length - 300) : next;
    });
  }, []);

  const clearTranscripts = useCallback(() => {
    setTranscripts([]);
  }, []);

  const resetPlaybackState = useCallback(() => {
    playbackAtRef.current = 0;
    playbackPendingRef.current = new Int16Array(0);
    playbackDrainingRef.current = false;
    if (playbackFlushTimerRef.current) {
      clearTimeout(playbackFlushTimerRef.current);
      playbackFlushTimerRef.current = null;
    }
  }, []);

  const ensureAudioContext = useCallback(async () => {
    let ctx = audioCtxRef.current;
    if (!ctx) {
      ctx = new AudioContext();
      audioCtxRef.current = ctx;
    }
    if (ctx.state === "suspended") {
      await ctx.resume();
    }
    return ctx;
  }, []);

  const closeAudioContext = useCallback(async () => {
    const ctx = audioCtxRef.current;
    audioCtxRef.current = null;
    if (ctx) {
      try {
        await ctx.close();
      } catch {
      }
    }
  }, []);

  const stopMic = useCallback(() => {
    if (scriptNodeRef.current) {
      scriptNodeRef.current.disconnect();
      scriptNodeRef.current.onaudioprocess = null;
    }
    if (micSinkGainRef.current) micSinkGainRef.current.disconnect();
    if (micSourceRef.current) micSourceRef.current.disconnect();
    if (micStreamRef.current) micStreamRef.current.getTracks().forEach((t) => t.stop());

    scriptNodeRef.current = null;
    micSinkGainRef.current = null;
    micSourceRef.current = null;
    micStreamRef.current = null;
    setMicActive(false);
  }, []);

  const schedulePlaybackChunk = useCallback((int16) => {
    if (!int16.length) return;
    const ctx = audioCtxRef.current;
    if (!ctx) return;

    const f32 = new Float32Array(int16.length);
    for (let i = 0; i < int16.length; i += 1) {
      f32[i] = int16[i] / 32768;
    }

    const audioBuffer = ctx.createBuffer(1, f32.length, TARGET_RATE);
    audioBuffer.copyToChannel(f32, 0);

    const source = ctx.createBufferSource();
    source.buffer = audioBuffer;
    source.connect(ctx.destination);

    const now = ctx.currentTime;
    playbackAtRef.current = Math.max(playbackAtRef.current, now + PLAYBACK_LEAD_SEC);
    source.start(playbackAtRef.current);
    playbackAtRef.current += audioBuffer.duration;
  }, []);

  const flushPlaybackQueue = useCallback(
    async (force = false) => {
      if (playbackDrainingRef.current) return;

      if (!force && playbackPendingRef.current.length < PLAYBACK_MIN_CHUNK_SAMPLES) {
        if (!playbackFlushTimerRef.current && playbackPendingRef.current.length > 0) {
          playbackFlushTimerRef.current = setTimeout(() => {
            playbackFlushTimerRef.current = null;
            void flushPlaybackQueue(false);
          }, PLAYBACK_FLUSH_DELAY_MS);
        }
        return;
      }

      playbackDrainingRef.current = true;
      try {
        await ensureAudioContext();
        while (playbackPendingRef.current.length > 0) {
          if (!force && playbackPendingRef.current.length < PLAYBACK_MIN_CHUNK_SAMPLES) break;
          const take = force
            ? playbackPendingRef.current.length
            : Math.min(playbackPendingRef.current.length, PLAYBACK_MAX_CHUNK_SAMPLES);
          const chunk = playbackPendingRef.current.slice(0, take);
          playbackPendingRef.current = playbackPendingRef.current.slice(take);
          schedulePlaybackChunk(chunk);
          force = false;
        }
      } finally {
        playbackDrainingRef.current = false;
      }

      if (playbackPendingRef.current.length > 0 && !playbackFlushTimerRef.current) {
        playbackFlushTimerRef.current = setTimeout(() => {
          playbackFlushTimerRef.current = null;
          void flushPlaybackQueue(false);
        }, PLAYBACK_FLUSH_DELAY_MS);
      }
    },
    [ensureAudioContext, schedulePlaybackChunk]
  );

  const playPcm16 = useCallback(
    async (arrayBuffer) => {
      await ensureAudioContext();
      const int16 = new Int16Array(arrayBuffer.slice(0));
      if (!int16.length) return;

      playbackPendingRef.current = concatInt16(playbackPendingRef.current, int16);
      if (playbackPendingRef.current.length >= PLAYBACK_MIN_CHUNK_SAMPLES) {
        await flushPlaybackQueue(false);
      } else if (!playbackFlushTimerRef.current) {
        playbackFlushTimerRef.current = setTimeout(() => {
          playbackFlushTimerRef.current = null;
          void flushPlaybackQueue(false);
        }, PLAYBACK_FLUSH_DELAY_MS);
      }
    },
    [ensureAudioContext, flushPlaybackQueue]
  );

  const ensureMicPermission = useCallback(async () => {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    stream.getTracks().forEach((t) => t.stop());
  }, []);

  const requestReauth = useCallback((message, { redirect = true } = {}) => {
    setErrorMessage(
      message ||
        (redirect
          ? "Authentication required. Redirecting to login..."
          : "Authentication required for voice session.")
    );
    setState(redirect ? "auth_required" : "auth_failed");
    if (!redirect) return;
    if (loginRedirectPendingRef.current) return;
    loginRedirectPendingRef.current = true;

    try {
      logout(false);
    } catch {
    }

    setTimeout(() => {
      login();
    }, 50);
  }, []);

  const ensureValidAccessToken = useCallback(async ({ forceRefresh = false, redirectOnMissing = true } = {}) => {
    try {
      await refreshIfNeeded(forceRefresh);
    } catch {
    }

    const token = String(getAccessToken() || "").trim();
    if (token) return token;

    if (redirectOnMissing) {
      requestReauth("Voice session expired. Redirecting to login...", { redirect: true });
    }
    return "";
  }, [requestReauth]);

  const startMic = useCallback(async () => {
    const service = serviceRef.current;
    if (!service || !sessionStartedRef.current) return;
    if (micStreamRef.current) return;

    await ensureAudioContext();
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        channelCount: 1,
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
    });

    const ctx = audioCtxRef.current;
    if (!ctx) {
      stream.getTracks().forEach((t) => t.stop());
      return;
    }

    micStreamRef.current = stream;
    micSourceRef.current = ctx.createMediaStreamSource(stream);
    scriptNodeRef.current = ctx.createScriptProcessor(2048, 1, 1);
    micSinkGainRef.current = ctx.createGain();
    micSinkGainRef.current.gain.value = 0;

    scriptNodeRef.current.onaudioprocess = (event) => {
      const serviceNow = serviceRef.current;
      if (!serviceNow || !sessionStartedRef.current) return;
      const inData = event.inputBuffer.getChannelData(0);
      const down = downsampleToTarget(inData, ctx.sampleRate);
      const pcm16 = float32ToInt16(down);
      if (pcm16.byteLength > 0) {
        try {
          serviceNow.sendAudio(pcm16.buffer);
        } catch {
        }
      }
    };

    micSourceRef.current.connect(scriptNodeRef.current);
    scriptNodeRef.current.connect(micSinkGainRef.current);
    micSinkGainRef.current.connect(ctx.destination);
    setMicActive(true);
  }, [ensureAudioContext]);

  const prepareSessionContent = useCallback(async (clientId) => {
    const id = String(clientId || "").trim();
    if (!id) return { prompt: "", firstMessage: "" };
    try {
      const res = await buildScenarioPrompt({ client_id: id });
      return {
        prompt: String(res?.prompt || "").trim(),
        firstMessage: String(res?.first_message ?? res?.firstMessage ?? "").trim(),
      };
    } catch {
      return { prompt: "", firstMessage: "" };
    }
  }, []);

  const buildSessionPrompt = useCallback((basePrompt, { clientName = "", isThirdPerson = false } = {}) => {
    const prompt = String(basePrompt || "").trim();
    const name = String(clientName || "").trim();
    if (!name) return prompt;

    const contactType = isThirdPerson ? "third_person" : "debtor";
    const context = [
      "Conversation context:",
      `- contact_name: ${name}`,
      `- contact_type: ${contactType}`,
      "Use this contact_name when addressing the person in the call.",
    ].join("\n");

    return prompt ? `${prompt}\n\n${context}` : context;
  }, []);

  const handleUnauthorized = useCallback(async () => {
    if (stopRequestedRef.current) return;
    if (retryInProgressRef.current) return;

    if (retryCountRef.current >= 1) {
      if (isAuthenticated()) {
        setState("auth_failed");
        setErrorMessage("Voice session was rejected by backend (unauthorized).");
        setWarningMessage("App session is still active. Retry the call or re-login manually.");
      } else {
        requestReauth("Unauthorized voice session. Redirecting to login...", { redirect: true });
      }
      return;
    }

    retryInProgressRef.current = true;
    retryCountRef.current += 1;
    setBusy(true);
    setState("retrying_auth");
    setWarningMessage("Session expired. Refreshing token and reconnecting...");

    try {
      const token = await ensureValidAccessToken({
        forceRefresh: true,
        redirectOnMissing: !isAuthenticated(),
      });
      if (!token) {
        if (isAuthenticated()) {
          setState("auth_failed");
          setErrorMessage("Unable to refresh token for voice reconnect.");
          setWarningMessage("App session is still active. Retry the call.");
        }
        return;
      }

      if (typeof openConnectionRef.current !== "function") {
        throw new Error("Reconnect function is not available.");
      }

      await openConnectionRef.current(token, systemPromptRef.current, sessionVarsRef.current);
      setWarningMessage("Session reconnected after token refresh.");
    } catch {
      if (isAuthenticated()) {
        setState("auth_failed");
        setErrorMessage("Voice reconnect failed after unauthorized response.");
        setWarningMessage("App session is still active. Retry the call.");
      } else {
        requestReauth("Voice reconnect failed. Redirecting to login...", { redirect: true });
      }
    } finally {
      retryInProgressRef.current = false;
      setBusy(false);
    }
  }, [ensureValidAccessToken, requestReauth]);

  const handleJsonEvent = useCallback(
    (evt) => {
      const t = String(evt?.type || "").trim();

      if (t === "session_started") {
        sessionStartedRef.current = true;
        setSessionStarted(true);
        resetPlaybackState();
        setState("listening");
        setErrorMessage("");
        void startMic().catch((err) => {
          setState("mic_error");
          setErrorMessage(err?.message || "Microphone start failed.");
        });
        return;
      }

      if (t === "warning") {
        const code = String(evt?.code || "").trim();
        const msg = String(evt?.message || evt?.detail || `warning:${code || "-"}`).trim();
        setWarningMessage(msg);
        setState(`warning:${code || "-"}`);
        return;
      }

      if (t === "error") {
        const code = String(evt?.code || "").trim();
        const msg = String(evt?.message || evt?.detail || `error:${code || "-"}`).trim();
        setErrorMessage(msg);
        setState(`error:${code || "-"}`);

        if (code.toLowerCase() === "unauthorized") {
          unauthorizedPendingRef.current = true;
          const service = serviceRef.current;
          if (service) {
            service.close(1008, "unauthorized");
          } else {
            void handleUnauthorized();
          }
        }
        return;
      }

      if (t === "session_ended") {
        sessionStartedRef.current = false;
        setSessionStarted(false);
        stopMic();
        resetPlaybackState();
        setState(`ended:${evt?.reason || "unknown"}`);
        return;
      }

      if (t === "user_transcript") {
        addTranscript("user", evt?.text || "");
        return;
      }

      if (t === "assistant_transcript") {
        addTranscript("bot", evt?.text || "");
      }
    },
    [addTranscript, handleUnauthorized, resetPlaybackState, startMic, stopMic]
  );

  const openConnection = useCallback(
    async (token, systemPrompt = "", sessionVars = {}) => {
      if (!wsUrl) throw new Error("Missing WebSocket URL.");

      if (isInsecureProdWsUrl(wsUrl)) {
        setWarningMessage((prev) => {
          if (prev) return prev;
          return "Production warning: use wss:// endpoint for /ws/bot.";
        });
      }

      if (serviceRef.current) {
        try {
          serviceRef.current.close(1000, "reconnect_replace");
        } catch {
        }
        serviceRef.current = null;
      }

      const service = createVoicebotWsService({
        wsUrl,
        onOpen: () => {
          setConnected(true);
          setState("open_wait_start");
        },
        onJsonEvent: handleJsonEvent,
        onBinaryAudio: (buffer) => {
          void playPcm16(buffer).catch(() => {
            setState("audio_playback_error");
            setErrorMessage("Audio playback failed.");
          });
        },
        onError: (evt) => {
          const code = String(evt?.code || "socket_error").trim();
          setState(`ws_error:${code}`);
        },
        onClose: (event) => {
          if (serviceRef.current === service) {
            serviceRef.current = null;
          }

          sessionStartedRef.current = false;
          setSessionStarted(false);
          setConnected(false);
          stopMic();
          resetPlaybackState();

          const closeReason = String(event?.reason || "").toLowerCase();
          const unauthorizedClose =
            unauthorizedPendingRef.current ||
            event?.code === 1008 ||
            closeReason.includes("unauthorized");
          unauthorizedPendingRef.current = false;

          if (stopRequestedRef.current) {
            stopRequestedRef.current = false;
            setState("idle");
            return;
          }

          if (unauthorizedClose) {
            void handleUnauthorized();
            return;
          }

          setState(event?.code === 1000 ? "closed" : "closed_warn");
        },
      });

      serviceRef.current = service;
      setState("connecting");
      await service.connect(token, systemPrompt, sessionVars);
    },
    [handleJsonEvent, handleUnauthorized, playPcm16, resetPlaybackState, stopMic, wsUrl]
  );

  useEffect(() => {
    openConnectionRef.current = openConnection;
  }, [openConnection]);

  const startSession = useCallback(
    async ({ clientId, clientName = "", isThirdPerson = false } = {}) => {
      if (connected || busy) return;

      const id = String(clientId || "").trim();
      if (!id) {
        setErrorMessage("Missing client id.");
        return;
      }

      setBusy(true);
      setErrorMessage("");
      setWarningMessage("");
      setState("preparing");

      stopRequestedRef.current = false;
      unauthorizedPendingRef.current = false;
      retryCountRef.current = 0;
      retryInProgressRef.current = false;
      loginRedirectPendingRef.current = false;

      try {
        await ensureMicPermission();
        const { prompt: basePrompt, firstMessage } = await prepareSessionContent(id);
        const normalizedName = String(clientName || "").trim();
        const vars = {
          client_id: id,
          client_name: normalizedName,
          contact_name: normalizedName,
          is_third_person: !!isThirdPerson,
        };
        if (firstMessage) {
          vars.first_message = firstMessage;
        }
        sessionVarsRef.current = vars;
        systemPromptRef.current = buildSessionPrompt(basePrompt, {
          clientName: normalizedName,
          isThirdPerson: !!isThirdPerson,
        });

        sessionStartedRef.current = false;
        setSessionStarted(false);
        resetPlaybackState();

        const token = await ensureValidAccessToken({
          forceRefresh: false,
          redirectOnMissing: true,
        });
        if (!token) return;

        await openConnection(token, systemPromptRef.current, vars);
      } catch (err) {
        setConnected(false);
        sessionStartedRef.current = false;
        setSessionStarted(false);
        setState("idle");
        setErrorMessage(err?.message || "Failed to start voice session.");
      } finally {
        setBusy(false);
      }
    },
    [
      busy,
      connected,
      ensureMicPermission,
      ensureValidAccessToken,
      openConnection,
      prepareSessionContent,
      buildSessionPrompt,
      resetPlaybackState,
    ]
  );

  const stopSession = useCallback(async () => {
    setBusy(true);
    stopRequestedRef.current = true;
    unauthorizedPendingRef.current = false;
    retryInProgressRef.current = false;
    retryCountRef.current = 0;

    try {
      if (serviceRef.current) {
        serviceRef.current.stop();
      }
    } catch {
    } finally {
      serviceRef.current = null;
      systemPromptRef.current = "";
      sessionVarsRef.current = {};
      sessionStartedRef.current = false;
      setSessionStarted(false);
      setConnected(false);
      stopMic();
      resetPlaybackState();
      await closeAudioContext();
      setState("idle");
      setBusy(false);
    }
  }, [closeAudioContext, resetPlaybackState, stopMic]);

  useEffect(() => {
    sessionStartedRef.current = sessionStarted;
  }, [sessionStarted]);

  useEffect(() => {
    return () => {
      stopRequestedRef.current = true;
      unauthorizedPendingRef.current = false;
      retryInProgressRef.current = false;

      try {
        if (serviceRef.current) {
          serviceRef.current.close(1000, "component_unmount");
        }
      } catch {
      }
      serviceRef.current = null;
      systemPromptRef.current = "";
      sessionVarsRef.current = {};

      stopMic();
      resetPlaybackState();

      const ctx = audioCtxRef.current;
      audioCtxRef.current = null;
      if (ctx) {
        void ctx.close().catch(() => {
        });
      }
    };
  }, [resetPlaybackState, stopMic]);

  return {
    state,
    busy,
    connected,
    sessionStarted,
    micActive,
    transcripts,
    errorMessage,
    warningMessage,
    startSession,
    stopSession,
    clearTranscripts,
  };
}
